import type { Category, CategoryWithItems } from '@gov-nx/module/service';

/**
 * Service for managing category selection state
 * Supports two modes:
 * - allSelected: All categories are selected (empty selection array)
 * - Partial selection: Only selected items are tracked in the array
 */
export class CategorySelectionService {
	private readonly selected: Set<string>;
	private allSelected: boolean;
	private readonly allCategories: Map<string, Category>;

	constructor(
		categories: Record<string, Category | undefined>,
		initial: string[] = [],
		allSelected: boolean = true
	) {
		this.selected = new Set(initial);
		this.allSelected = allSelected;
		this.allCategories = new Map();

		// Register categories during initialization
		Object.entries(categories).forEach(([key, category]) => {
			if (category) {
				this.allCategories.set(key, category);
			}
		});
	}

	getSelected(): string[] {
		return Array.from(this.selected);
	}

	isAllSelected(): boolean {
		return this.allSelected;
	}

	/**
	 * Type guard to check if category has items array
	 */
	private isCategoryWithItems(
		category: Category
	): category is CategoryWithItems {
		return 'items' in category;
	}

	/**
	 * Checks if a category has subcategories (items array)
	 */
	private hasSubcategories(category: Category): boolean {
		return this.isCategoryWithItems(category);
	}

	/**
	 * Toggles selection state of an entire category
	 * When in allSelected mode, switches to partial mode and deselects the toggled category
	 */
	toggleCategory(categoryKey: string, category: Category): string[] {
		// If all selected, switch to individual selection mode
		if (this.allSelected) {
			this.allSelected = false;
			this.selected.clear();

			// Select all categories EXCEPT the one being unchecked
			this.allCategories.forEach((cat, key) => {
				if (key === categoryKey) {
					// Don't add this category (user unchecked it)
					return;
				}

				// Add other categories
				if (this.isCategoryWithItems(cat)) {
					// Add all individual items
					cat.items.forEach((item) => {
						this.selected.add(`${key}:${item.id}`);
					});
				} else {
					// For simple categories, add KEY:ID format
					const id = (cat as Record<string, string>).id;
					if (id) {
						this.selected.add(`${key}:${id}`);
					}
				}
			});

			return this.getSelected();
		}

		const hasSubs = this.hasSubcategories(category);

		if (!hasSubs) {
			// For simple categories, use KEY:ID format
			const id = (category as Record<string, string>).id;
			const selectionKey = id ? `${categoryKey}:${id}` : categoryKey;

			if (this.selected.has(selectionKey)) {
				this.selected.delete(selectionKey);
			} else {
				this.selected.add(selectionKey);
			}

			this.checkIfAllSelected();
			return this.getSelected();
		}

		if (!this.isCategoryWithItems(category)) {
			return this.getSelected();
		}

		// Check if all items are currently selected
		const allItemsSelected = category.items.every((item) =>
			this.selected.has(`${categoryKey}:${item.id}`)
		);

		if (allItemsSelected) {
			// Remove all items
			category.items.forEach((item) =>
				this.selected.delete(`${categoryKey}:${item.id}`)
			);
		} else {
			// Add all items
			category.items.forEach((item) =>
				this.selected.add(`${categoryKey}:${item.id}`)
			);
		}

		this.checkIfAllSelected();
		return this.getSelected();
	}

	/**
	 * Toggles selection state of a subcategory within a category
	 */
	toggleSubcategory(
		categoryKey: string,
		category: Category,
		subcategoryId: string
	): string[] {
		// If all selected, switch to individual selection mode
		if (this.allSelected) {
			this.allSelected = false;
			this.selected.clear();

			// Select all categories
			this.allCategories.forEach((cat, key) => {
				if (key === categoryKey) {
					// For this category, select all items EXCEPT the unchecked one
					if (this.isCategoryWithItems(cat)) {
						cat.items.forEach((item) => {
							if (item.id !== subcategoryId) {
								this.selected.add(`${key}:${item.id}`);
							}
						});
					} else {
						// For simple categories, add KEY:ID format
						const id = (cat as Record<string, string>).id;
						if (id && id !== subcategoryId) {
							this.selected.add(`${key}:${id}`);
						}
					}
				} else {
					// Select other categories entirely
					if (this.isCategoryWithItems(cat)) {
						cat.items.forEach((item) => {
							this.selected.add(`${key}:${item.id}`);
						});
					} else {
						// For simple categories, add KEY:ID format
						const id = (cat as Record<string, string>).id;
						if (id) {
							this.selected.add(`${key}:${id}`);
						}
					}
				}
			});

			return this.getSelected();
		}

		if (!this.isCategoryWithItems(category)) return this.getSelected();

		const subId = `${categoryKey}:${subcategoryId}`;

		// Toggle specific subcategory
		if (this.selected.has(subId)) {
			this.selected.delete(subId);
		} else {
			this.selected.add(subId);
		}

		this.checkIfAllSelected();
		return this.getSelected();
	}

	/**
	 * Toggles between selecting all categories and deselecting all categories
	 * If allSelected is true, deselects all. If false, selects all.
	 */
	toggleAll(): void {
		this.allSelected = !this.allSelected;
		this.selected.clear();
	}

	/**
	 * Returns all categories in expanded format (individual items)
	 * Used when allSelected is true to get the actual list of selected items
	 */
	getAllCategoriesExpanded(): string[] {
		const result: string[] = [];

		this.allCategories.forEach((category, key) => {
			if (this.isCategoryWithItems(category)) {
				// For categories with items, add all individual items
				category.items.forEach((item) => {
					result.push(`${key}:${item.id}`);
				});
			} else {
				// For simple categories, add KEY:ID format
				const id = (category as Record<string, string>).id;
				if (id) {
					result.push(`${key}:${id}`);
				}
			}
		});

		return result;
	}

	/**
	 * Returns selected categories in expanded format
	 * If allSelected is true, returns all categories
	 * Otherwise returns only explicitly selected items
	 */
	getSelectedExpanded(): string[] {
		if (this.allSelected) {
			return this.getAllCategoriesExpanded();
		}
		return Array.from(this.selected);
	}

	/**
	 * Checks if a specific category or subcategory is selected
	 * For categories with items, checks if all items are selected
	 * For simple categories, checks if KEY:ID is selected
	 */
	isSelected(id: string): boolean {
		if (this.allSelected) return true;

		// Check if this is a category key (not a subcategory ID)
		const category = this.allCategories.get(id);
		if (category) {
			if (this.isCategoryWithItems(category)) {
				// Category is selected if all its items are selected
				return category.items.every((item) =>
					this.selected.has(`${id}:${item.id}`)
				);
			} else {
				// For simple categories, check KEY:ID format
				const catId = (category as Record<string, string>).id;
				const selectionKey = catId ? `${id}:${catId}` : id;
				return this.selected.has(selectionKey);
			}
		}

		// Otherwise it's a subcategory ID (format: categoryKey:itemId)
		return this.selected.has(id);
	}

	/**
	 * Checks if a category is in an indeterminate state
	 * Returns true if some (but not all) items in the category are selected
	 * For simple categories without items, always returns false
	 */
	isIndeterminate(categoryKey: string): boolean {
		// If all selected, no category is indeterminate
		if (this.allSelected) return false;

		const category = this.allCategories.get(categoryKey);
		if (!category) return false;

		// Simple categories cannot be indeterminate
		if (!this.isCategoryWithItems(category)) return false;

		// Count how many items are selected
		const selectedCount = category.items.filter((item) =>
			this.selected.has(`${categoryKey}:${item.id}`)
		).length;

		// Indeterminate if some (but not all) items are selected
		return selectedCount > 0 && selectedCount < category.items.length;
	}

	/**
	 * Checks if all categories are selected and updates allSelected flag accordingly
	 * If all categories are selected individually, switches to allSelected mode
	 */
	private checkIfAllSelected(): void {
		if (this.allSelected) return;

		const allCategoriesSelected = Array.from(
			this.allCategories.entries()
		).every(([key, category]) => {
			if (this.isCategoryWithItems(category)) {
				// All individual items must be selected
				return category.items.every((item) =>
					this.selected.has(`${key}:${item.id}`)
				);
			} else {
				// For simple categories, check KEY:ID format
				const catId = (category as Record<string, string>).id;
				const selectionKey = catId ? `${key}:${catId}` : key;
				return this.selected.has(selectionKey);
			}
		});

		if (allCategoriesSelected && this.allCategories.size > 0) {
			// All categories selected → switch to allSelected mode
			this.allSelected = true;
			this.selected.clear();
		}
	}
}
